import {
  CSSProperties, ElementType, HTMLAttributes, KeyboardEvent as ReactKeyboardEvent, ReactNode,
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { COARSE, NARROW, PHONE, useMediaQuery } from '../shared/media';

/**
 * The design system's interactive pieces (see Design System.dc.html):
 * one centered modal — the ONE place edits happen — and one row overflow menu.
 * Opening either is the caller's job; both close on ESC / click-outside.
 *
 * Mobile foundation (docs/PLAYBOOK-mobile.md §3): under 600px the modal is a
 * bottom sheet (`.fs-modal.sheet`), the row menu is portalled to the body so
 * no scrolling card can clip it and is an action sheet on touch, and two
 * small shells — `TabStrip` and `ActionBar` — carry the scroll-into-view and
 * safe-area behaviour every screen phase reuses. Nothing here changes what a
 * desktop pointer sees.
 */

/** Centered modal: 520–640px, radius 12, footer `primary · Cancel` on #fcfdfe. */
export function Modal({ title, sub, narrow, wide, gallery, xl, aside, onClose, children, footer, className }: {
  title: ReactNode;
  /** A muted line under the title — what the dialog is for ("Plan, price and free period in one change."). */
  sub?: ReactNode;
  /** A class on the dialog box itself, for a caller's own body layout (R4: the field picker). */
  className?: string;
  /** 520px (single-column forms) instead of the 560px default. */
  narrow?: boolean;
  /** 640px, for a body of rows that each carry a control (the carry map). */
  wide?: boolean;
  /** 1120px and the full height, for a list beside a preview (the template gallery). */
  gallery?: boolean;
  /** 860px, for the New form modal's three modes (R2). */
  xl?: boolean;
  /** Quiet text between the title and the close button — "Step 2 of 3". */
  aside?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Rendered inside .fs-modal-foot; the primary action comes first. */
  footer?: ReactNode;
}) {
  // On a phone the same modal is a sheet pinned to the bottom edge (F4). The
  // class is what the CSS keys on and what a later phase can read back.
  const sheet = useMediaQuery(PHONE);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      {/* The second click of a double-click lands here when its first click
          was what opened the dialog (a workflow card): that is not a dismissal. */}
      <div className="fs-scrim" onClick={(e) => { if (e.detail > 1) return; onClose(); }} />
      <div className="fs-modal-wrap">
        <div
          className={`fs-modal${narrow ? ' narrow' : ''}${wide ? ' wide' : ''}${gallery ? ' gallery' : ''}${xl ? ' xl' : ''}${sheet ? ' sheet' : ''}${className ? ` ${className}` : ''}`}
          role="dialog" aria-modal="true"
        >
          <div className="fs-modal-head">
            <h3>{title}</h3>
            {aside && <span className="fs-modal-aside">{aside}</span>}
            <button className="fs-btn icon" title="Close" onClick={onClose}>✕</button>
          </div>
          {sub && <p className="fs-modal-sub">{sub}</p>}
          <div className="fs-modal-body">{children}</div>
          {footer && <div className="fs-modal-foot">{footer}</div>}
        </div>
      </div>
    </>
  );
}

const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The row `⋯` trigger and its anchored menu. The caller owns which row's menu
 * is open (one `openMenuId` per table), so opening one closes any other.
 *
 * The menu is rendered into `document.body` and positioned from the trigger's
 * rectangle (F5): inside the anchor it was clipped by any card or scroll
 * wrapper with an overflow, which is every table's last row. It still closes
 * on Escape and on a press outside; Tab from the trigger still lands on the
 * first item, and Tab past the last one closes the menu and returns to the
 * trigger, so the keyboard path reads the same as when the items were the
 * trigger's next siblings. On a coarse pointer it is an action sheet.
 */
export function RowMenu({ open, onToggle, children, label, renderTrigger, head, className }: {
  open: boolean;
  onToggle: (open: boolean) => void;
  /** fs-menu content: <button>, <a>, and <div className="rule" /> rows. */
  children: ReactNode;
  /** The row's title, shown as the sheet's header on touch. */
  label?: ReactNode;
  /**
   * A trigger other than the ⋯ button (R1: the shell's avatar). It must
   * render a <button> and spread the handlers it is given onto it.
   */
  renderTrigger?: (p: { open: boolean; onClick: () => void; onKeyDown: (e: ReactKeyboardEvent) => void }) => ReactNode;
  /** A header inside the menu itself, above the items (the avatar menu's name and role). */
  head?: ReactNode;
  /** Extra classes on the menu. */
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const coarse = useMediaQuery(COARSE);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || menuRef.current?.contains(t)) return;
      onToggle(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onToggle(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
  }, [open, onToggle]);

  // Where the menu goes: under the trigger, right edges aligned, as the
  // anchored version was — flipped above it only when it would leave the
  // viewport. Re-measured on scroll and resize so it stays on its row.
  useLayoutEffect(() => {
    if (!open || coarse) { setPos(null); return; }
    const place = () => {
      const btn = ref.current?.querySelector('button');
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const menuH = menuRef.current?.offsetHeight || 0;
      const below = r.bottom + 4;
      const flip = menuH > 0 && below + menuH > window.innerHeight && r.top - 4 - menuH >= 0;
      setPos({ top: flip ? r.top - 4 - menuH : below, right: Math.max(0, window.innerWidth - r.right) });
    };
    place();
    window.addEventListener('resize', place);
    document.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); document.removeEventListener('scroll', place, true); };
  }, [open, coarse]);

  const items = () => Array.from(menuRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) || []);
  const trigger = () => ref.current?.querySelector('button') as HTMLButtonElement | null;

  const onTriggerKey = (e: ReactKeyboardEvent) => {
    if (!open || e.key !== 'Tab' || e.shiftKey) return;
    const first = items()[0];
    if (first) { e.preventDefault(); first.focus(); }
  };
  const onMenuKey = (e: ReactKeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLElement);
    if (!e.shiftKey && at === list.length - 1) { e.preventDefault(); onToggle(false); trigger()?.focus(); }
    if (e.shiftKey && at === 0) { e.preventDefault(); trigger()?.focus(); }
  };

  const menu = open && (
    <>
      {coarse && <div className="fs-menu-scrim" onClick={() => onToggle(false)} />}
      <div
        ref={menuRef}
        className={`fs-menu portal${coarse ? ' sheet' : ''}${className ? ` ${className}` : ''}`}
        style={coarse ? undefined : { position: 'fixed', top: pos?.top ?? 0, right: pos?.right ?? 0, visibility: pos ? 'visible' : 'hidden' }}
        onClick={() => onToggle(false)}
        onKeyDown={onMenuKey}
      >
        {coarse && label && <div className="fs-menu-title">{label}</div>}
        {head && <div className="fs-menu-head">{head}</div>}
        {children}
      </div>
    </>
  );

  return (
    <span className="fs-menu-anchor" ref={ref}>
      {renderTrigger
        ? renderTrigger({ open, onClick: () => onToggle(!open), onKeyDown: onTriggerKey })
        : <button type="button" className={`fs-btn icon${open ? ' open' : ''}`} title="More actions" onClick={() => onToggle(!open)} onKeyDown={onTriggerKey}>⋯</button>}
      {menu && typeof document !== 'undefined' ? createPortal(menu, document.body) : null}
    </span>
  );
}

/**
 * A row of tabs that scrolls sideways when it is wider than its box (F3).
 *
 * Renders exactly the element it is given (`nav.fw-tabs`, `div.fs-tabs`…) —
 * the styling stays with the strip's own class. On mount, and whenever the
 * strip re-renders, the active tab is scrolled into view horizontally, and
 * `data-overflow` says which edge still hides tabs so the CSS can fade it.
 * A strip that fits (every desktop strip) is left exactly alone: no scroll,
 * no attribute.
 */
export function TabStrip({ as: Tag = 'nav', children, ...rest }: {
  as?: ElementType;
  children: ReactNode;
} & HTMLAttributes<HTMLElement>) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const more = el.scrollWidth - el.clientWidth;
      if (more <= 1) { if (el.dataset.overflow) delete el.dataset.overflow; return; }
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft < more - 1;
      el.dataset.overflow = left && right ? 'both' : left ? 'left' : right ? 'right' : 'none';
    };
    const active = el.querySelector<HTMLElement>('.active, .on, [aria-current], [aria-selected="true"]');
    if (active && el.scrollWidth > el.clientWidth + 1) {
      // Centre it: the neighbours on both sides are the cue that there are more.
      el.scrollLeft = Math.max(0, active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2);
    }
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  });

  const T = Tag as ElementType;
  return <T ref={ref} {...rest}>{children}</T>;
}

/**
 * A panel that slides in from the right edge over a scrim (R3: the Publish
 * panel; the assistant draws its own on the same `.fs-drawer` classes). Esc
 * and the scrim close it, the way `Modal` does; under 600 it is the whole
 * width (controls.css F4). `narrow` is the design's 400px.
 */
export function Drawer({ title, narrow, onClose, children, footer, label, className }: {
  title: ReactNode;
  narrow?: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** The accessible name when the title is not plain text. */
  label?: string;
  className?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // The top-most overlay only (Appendix B): nothing under it hears this.
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <>
      <div className="fs-scrim" onClick={onClose} />
      <div
        className={`fs-drawer${narrow ? ' narrow' : ''}${className ? ` ${className}` : ''}`}
        role="dialog" aria-modal="true" aria-label={label || (typeof title === 'string' ? title : undefined)}
      >
        <div className="fs-drawer-head fs-drawer-titlerow">
          <h3 className="fs-drawer-title">{title}</h3>
          <button className="fs-btn icon" title="Close" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="fs-drawer-body">{children}</div>
        {footer && <div className="fs-drawer-foot">{footer}</div>}
      </div>
    </>
  );
}

/**
 * A bar pinned to the bottom of the viewport (F11): the form workspace's
 * draft bar on a phone, the submissions bulk-select bar, the public form's
 * pager, a settings save bar. While one is mounted the toasts sit above it
 * rather than on it — the bar's height is published as `--fs-action-bar-h`
 * on the root element, which `.fm-toasts` adds to its bottom offset.
 */
export function ActionBar({ children, className, style }: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const set = () => root.style.setProperty('--fs-action-bar-h', `${el.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    ro?.observe(el);
    return () => { ro?.disconnect(); root.style.removeProperty('--fs-action-bar-h'); };
  }, []);

  return <div ref={ref} className={`fs-action-bar${className ? ` ${className}` : ''}`} style={style}>{children}</div>;
}

/**
 * The save bar under a column of settings cards (Access, Document, per-form
 * Settings), pinned to the bottom edge on a phone (F11).
 *
 * On a desktop it renders exactly the `div.fs-save-bar` those three tabs
 * rendered before — same element, same class, no wrapper — so nothing about
 * the desktop, or the tests that read it, changes. Under 600px it is an
 * `ActionBar` as well, which is what gives it the safe-area padding and lifts
 * the toasts off the button they would otherwise cover.
 */
export function SaveBar({ children }: { children: ReactNode }) {
  const phone = useMediaQuery(PHONE);
  return phone
    ? <ActionBar className="fs-save-bar">{children}</ActionBar>
    : <div className="fs-save-bar">{children}</div>;
}

/* ===========================================================================
   Redesign building blocks (R0, docs/PLAYBOOK-redesign.md §1).

   The pieces every settings and platform page is rebuilt from, from R10 on:
   a label/value/actions row, a titled table card, a figure tile, a code
   block, a section label, a sub-tab row and a grouped side nav — plus the
   32×18 toggle the handoff draws everywhere. Each keeps the mobile
   foundation's rules: the table card only scrolls under 900 (trap 2), the
   sub-tabs ride on TabStrip so they scroll on a phone, and the side nav is a
   chip row under 900. Styles live in tokens/controls.css.
   =========================================================================== */

/** One row of a settings card: a label column, the value, right-aligned actions. */
export function RowCard({ label, hint, children, actions, className }: {
  label: ReactNode;
  /** Quiet text under the label. */
  hint?: ReactNode;
  /** The value column. */
  children?: ReactNode;
  /** Buttons or links, right-aligned; under 600 they drop below the value. */
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`fs-rowcard${className ? ` ${className}` : ''}`}>
      <div className="fs-rowcard-label">
        <div className="fs-rowcard-title">{label}</div>
        {hint && <div className="hint">{hint}</div>}
      </div>
      <div className="fs-rowcard-value">{children}</div>
      {actions && <div className="fs-rowcard-actions">{actions}</div>}
    </div>
  );
}

/**
 * A titled card holding one table: the title, an optional "+ Add" on the
 * right, and a wrapper that scrolls sideways only under 900px, so the
 * card's shadow-free border is never clipped on a desktop (trap 2). Put row
 * actions in a `td.fs-row-actions` and they right-align.
 */
export function TableCard({ title, hint, action, children, footer, className }: {
  /** Absent (with no action) for a table that stands under its own controls, as the Runs page does. */
  title?: ReactNode;
  hint?: ReactNode;
  /** The head's right-hand control — usually a "+ Add …" button. */
  action?: ReactNode;
  /** The table (`.fs-table`). */
  children: ReactNode;
  /** Notes, warnings or pagination, in an .fs-card-foot. */
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <section className={`fs-card fs-tablecard${className ? ` ${className}` : ''}`}>
      {(title || hint || action) && (
        <div className="fs-tablecard-head">
          <div className="text">
            {title && <h2>{title}</h2>}
            {hint && <div className="hint">{hint}</div>}
          </div>
          {action && <div className="fs-tablecard-action">{action}</div>}
        </div>
      )}
      <div className={`fs-tablecard-scroll${title || hint || action ? '' : ' bare'}`}>{children}</div>
      {footer && <div className="fs-card-foot">{footer}</div>}
    </section>
  );
}

/**
 * A figure with a caption and an optional usage bar: the billing meters and
 * the platform's MRR / Paying / Trials tiles. The bar is drawn only when the
 * limit is a number; an unlimited meter shows the figure alone.
 */
export function FigureTile({ label, value, caption, tone, usage, note, noteTone, className }: {
  label: ReactNode;
  value: ReactNode;
  caption?: ReactNode;
  /** Colours the caption. `info` is the blue of a free period (074). */
  tone?: 'ok' | 'warn' | 'danger' | 'muted' | 'info';
  /**
   * The bar: `used` against `limit`. `near` is the percentage at which it
   * turns amber (default 90) — the billing meters warn from 80 (R11).
   */
  usage?: { used: number; limit: number | null; near?: number };
  /** A line under the bar — what the number means for you ("38 left"). */
  note?: ReactNode;
  /** Colours the note. */
  noteTone?: 'ok' | 'warn' | 'danger' | 'muted';
  className?: string;
}) {
  // The state is decided on the exact share, so 99.6% is never drawn "over"
  // and 79.6% never "near"; only the drawn width is rounded.
  const pct = usage && usage.limit != null && usage.limit > 0 ? (usage.used / usage.limit) * 100 : null;
  const bar = pct != null ? Math.max(0, Math.min(100, Math.round(pct))) : null;
  const near = usage?.near ?? 90;
  return (
    <div className={`fs-tile${className ? ` ${className}` : ''}`}>
      <span className="fs-tile-label">{label}</span>
      <b className="fs-tile-value">{value}</b>
      {caption && <span className={`fs-tile-caption${tone ? ` ${tone}` : ''}`}>{caption}</span>}
      {bar != null && (
        <div
          className={`fs-tile-bar${pct! >= 100 ? ' over' : pct! >= near ? ' near' : ''}`}
          role="progressbar" aria-valuemin={0} aria-valuemax={usage!.limit!} aria-valuenow={usage!.used}
        >
          <div style={{ width: `${bar}%` }} />
        </div>
      )}
      {note && <span className={`fs-tile-note${noteTone ? ` ${noteTone}` : ''}`}>{note}</span>}
    </div>
  );
}

/** A block of code or a command in mono, with a Copy button. */
export function CodeBlock({ code, children, copy = true, label, className }: {
  /** What Copy puts on the clipboard. */
  code: string;
  /** What is shown; defaults to `code`. */
  children?: ReactNode;
  copy?: boolean;
  /** Names the block for a screen reader ("cURL example"). */
  label?: string;
  className?: string;
}) {
  const [done, setDone] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const onCopy = async () => {
    try { await navigator.clipboard?.writeText(code); } catch { /* the block is still selectable */ }
    setDone(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDone(false), 1500);
  };
  return (
    <div className={`fs-code${className ? ` ${className}` : ''}`}>
      <pre aria-label={label}><code>{children ?? code}</code></pre>
      {copy && (
        <button type="button" className="fs-btn sm neutral fs-code-copy" onClick={onCopy} aria-live="polite">
          {done ? 'Copied' : 'Copy'}
        </button>
      )}
    </div>
  );
}

/** The small uppercase label above a group of rows or a nav section. */
export function SectionLabel({ children, as: Tag = 'div', className, ...rest }: {
  as?: ElementType;
  children: ReactNode;
  className?: string;
} & HTMLAttributes<HTMLElement>) {
  const T = Tag as ElementType;
  return <T className={`fs-section-label${className ? ` ${className}` : ''}`} {...rest}>{children}</T>;
}

export interface SubTabItem {
  label: ReactNode;
  /** A route; the tab is a NavLink and lights up on it. */
  to?: string;
  /** NavLink's `end`: match the path exactly. */
  end?: boolean;
  /** For a tab that is not a route: is it the current one? */
  active?: boolean;
  onClick?: () => void;
  /** A count or pill after the label. */
  badge?: ReactNode;
  /** A plain number after the label, in the faint caption colour (Site content's "Screenshots 9"). */
  count?: number;
}

/**
 * The 38px sub-tab row under a page header or the form bar: a 2px accent
 * underline on the current tab. Built on TabStrip, so it scrolls sideways on
 * a phone with the current tab scrolled into view.
 */
export function SubTabs({ items, className, ...rest }: {
  items: SubTabItem[];
  className?: string;
} & HTMLAttributes<HTMLElement>) {
  return (
    <TabStrip as="nav" className={`fs-tabs fs-subtabs${className ? ` ${className}` : ''}`} {...rest}>
      {items.map((it, i) => it.to
        ? (
          <NavLink key={i} to={it.to} end={it.end} className={({ isActive }) => (isActive ? 'active' : '')}>
            {it.label}{it.badge != null && <span className="fs-subtab-badge">{it.badge}</span>}
            {it.count != null && <span className="fs-subtab-count">{it.count}</span>}
          </NavLink>
        ) : (
          <button
            key={i} type="button" className={it.active ? 'active' : ''}
            aria-current={it.active ? 'page' : undefined} onClick={it.onClick}
          >
            {it.label}{it.badge != null && <span className="fs-subtab-badge">{it.badge}</span>}
            {it.count != null && <span className="fs-subtab-count">{it.count}</span>}
          </button>
        ))}
    </TabStrip>
  );
}

export interface SideNavItem {
  to: string;
  label: ReactNode;
  end?: boolean;
  badge?: ReactNode;
  /** Role gating: a hidden item is not rendered at all. */
  hidden?: boolean;
  /**
   * Path prefixes on which the item stays lit although its own route does
   * not match — a page that belongs to the section without living under its
   * path. The platform's Accounts is `/platform` with `end`, and still lit on
   * `/platform/accounts/:id/billing` (R12). A prefix matches itself and
   * anything below it (`/a/b` matches `/a/b` and `/a/b/c`, not `/a/bc`).
   */
  also?: string[];
}
export interface SideNavGroup {
  /** The group's section label; a group without one is a plain list. */
  label?: ReactNode;
  items: SideNavItem[];
}

/**
 * A grouped sidebar of links — the settings and platform navigation from
 * R10 on. Under 900px the same items render as one scrolling chip row (a
 * TabStrip), so the desktop markup is untouched and the phone gets a row it
 * can thumb through. Hidden items (role gating) never render in either.
 */
export function SideNav({ groups, className, ...rest }: {
  groups: SideNavGroup[];
  className?: string;
} & HTMLAttributes<HTMLElement>) {
  const narrow = useMediaQuery(NARROW);
  const { pathname } = useLocation();
  const visible = groups.map((g) => ({ ...g, items: g.items.filter((it) => !it.hidden) })).filter((g) => g.items.length);
  const alsoLit = (it: SideNavItem) => !!it.also?.some((p) => {
    const base = p.replace(/\/+$/, '');
    return pathname === base || pathname.startsWith(`${base}/`);
  });
  const link = (it: SideNavItem, cls: string) => (
    <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => `${cls}${isActive || alsoLit(it) ? ' on' : ''}`}>
      <span className="label">{it.label}</span>
      {it.badge != null && <span className="n">{it.badge}</span>}
    </NavLink>
  );
  if (narrow) {
    return (
      <TabStrip as="nav" className={`fs-sidenav-chips${className ? ` ${className}` : ''}`} {...rest}>
        {visible.flatMap((g) => g.items.map((it) => link(it, 'fs-sidenav-chip')))}
      </TabStrip>
    );
  }
  return (
    <nav className={`fs-sidenav${className ? ` ${className}` : ''}`} {...rest}>
      {visible.map((g, i) => (
        <div className="fs-sidenav-group" key={i}>
          {g.label && <SectionLabel>{g.label}</SectionLabel>}
          {g.items.map((it) => link(it, 'fs-rail-item'))}
        </div>
      ))}
    </nav>
  );
}

/** The 32×18 switch: a real checkbox with role="switch" under a drawn track. */
export function Toggle({ checked, onChange, label, disabled, id, className }: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  id?: string;
  className?: string;
}) {
  return (
    <label className={`fs-toggle${disabled ? ' disabled' : ''}${className ? ` ${className}` : ''}`}>
      <input
        type="checkbox" role="switch" id={id} checked={checked} disabled={disabled}
        aria-checked={checked} onChange={(e) => { if (!disabled) onChange(e.target.checked); }}
      />
      <span className="track" aria-hidden="true" />
      {label != null && <span className="fs-toggle-label">{label}</span>}
    </label>
  );
}
